import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { setPermissionDebugMode, getPermissionDetails } from "services/user";

const PermissionDebugger = ({ permissionId, children, className = "" }) => {
  const [debugEnabled, setDebugEnabled] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [permissionInfo, setPermissionInfo] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);

  // Only show in development or local environment
  const isDev =
    import.meta.env.DEV ||
    window.location.hostname.includes("localhost") ||
    window.location.hostname.includes("detailslocal.com");

  useEffect(() => {
    // Load debug mode state from localStorage or ENV
    const savedDebugMode = localStorage.getItem("permissionDebugMode");
    const debugMode =
      savedDebugMode !== null
        ? savedDebugMode === "true"
        : import.meta.env.VITE_PERMISSION_DEBUG_MODE === "true";

    setDebugEnabled(debugMode);
    setPermissionDebugMode(debugMode);
  }, []);

  useEffect(() => {
    if (permissionId) {
      const details = getPermissionDetails(permissionId);
      setPermissionInfo(details);
    }
  }, [permissionId, debugEnabled]);

  // eslint-disable-next-line no-unused-vars
  const toggleDebugMode = () => {
    const newMode = !debugEnabled;
    setDebugEnabled(newMode);
    setPermissionDebugMode(newMode);
    localStorage.setItem("permissionDebugMode", newMode);
  };

  if (!isDev) {
    return <>{children}</>;
  }

  return (
    <div className={`relative block ${className}`.trim()}>
      {debugEnabled && permissionInfo && (
        <div
          ref={buttonRef}
          className="absolute -top-2 -right-2 z-50 cursor-pointer"
          onMouseEnter={() => {
            if (buttonRef.current) {
              const rect = buttonRef.current.getBoundingClientRect();
              const tooltipWidth = 250;
              const spacing = 6;

              // Calculate position - prefer right-aligned with button
              let left = rect.right + window.scrollX - tooltipWidth;

              // Ensure tooltip doesn't go off-screen to the left
              if (left < window.scrollX + 10) {
                left = rect.left + window.scrollX + spacing;
              }

              // Ensure tooltip doesn't go off-screen to the right
              const maxLeft =
                window.scrollX + window.innerWidth - tooltipWidth - 10;
              if (left > maxLeft) {
                left = maxLeft;
              }

              setTooltipPosition({
                top: rect.bottom + window.scrollY + spacing,
                left: left,
              });
            }
            setShowTooltip(true);
          }}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <div
            className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
              permissionInfo.hasPermission
                ? "bg-success text-white"
                : "bg-danger text-white"
            }`}
            title="Permission Info"
          >
            P
          </div>
        </div>
      )}
      {children}
      {showTooltip &&
        permissionInfo &&
        createPortal(
          <div
            className="fixed bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg min-w-[250px] z-[9999]"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
            }}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <div className="space-y-1">
              <div className="font-semibold border-b border-gray-700 pb-1 mb-2">
                Permission Details
              </div>
              <div>
                <span className="text-text-subtle">ID:</span>{" "}
                <span className="font-mono">{permissionInfo.id}</span>
              </div>
              <div>
                <span className="text-text-subtle">Path:</span>{" "}
                <span className="font-mono text-xs">{permissionInfo.name}</span>
              </div>
              <div>
                <span className="text-text-subtle">Display:</span>{" "}
                <span>{permissionInfo.displayName}</span>
              </div>
              <div>
                <span className="text-text-subtle">Has Access:</span>{" "}
                <span
                  className={`font-semibold ${
                    permissionInfo.hasPermission
                      ? "text-green-400"
                      : "text-red-400"
                  }`}
                >
                  {permissionInfo.hasPermission ? "Yes" : "No"}
                </span>
              </div>
            </div>
            <div className="absolute -top-1 right-2 w-2 h-2 bg-gray-900 transform rotate-45"></div>
          </div>,
          document.body
        )}
    </div>
  );
};

// Global toggle button component
export const PermissionDebugToggle = () => {
  const [debugEnabled, setDebugEnabled] = useState(false);

  const isDev =
    import.meta.env.DEV ||
    window.location.hostname.includes("localhost") ||
    window.location.hostname.includes("detailslocal.com");

  useEffect(() => {
    // Load debug mode state from localStorage or ENV
    const savedDebugMode = localStorage.getItem("permissionDebugMode");
    const debugMode =
      savedDebugMode !== null
        ? savedDebugMode === "true"
        : import.meta.env.VITE_PERMISSION_DEBUG_MODE === "true";

    setDebugEnabled(debugMode);
  }, []);

  const toggleDebugMode = () => {
    const newMode = !debugEnabled;
    setDebugEnabled(newMode);
    setPermissionDebugMode(newMode);
    localStorage.setItem("permissionDebugMode", newMode);
    // Force re-render of the page
    window.location.reload();
  };

  if (!isDev) {
    return null;
  }

  return (
    <button
      onClick={toggleDebugMode}
      className={`fixed bottom-4 right-4 z-[9999] w-12 h-12 rounded-full shadow-lg flex items-center justify-center text-white font-bold text-sm transition-all hover:scale-110 ${
        debugEnabled
          ? "bg-success hover:bg-green-700"
          : "bg-gray-600 hover:bg-gray-700"
      }`}
      title={
        debugEnabled ? "Disable Permission Debug" : "Enable Permission Debug"
      }
    >
      {debugEnabled ? "✓" : "P"}
    </button>
  );
};

export default PermissionDebugger;
